import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageShell } from '@/components/ds';
import { StatusBadge } from '@/components/common/StatusBadge';
import { CommunicationPolicyBadge } from '@/components/common/CommunicationPolicyBadge';
import { agentAPI, agencyAPI, voiceAPI } from '@/lib/api';
import { isKRRole, isAgencyPrincipal, isAccountingRole, formatDate, getUserAgencyId, US_STATES, formatPhoneInput } from '@/lib/utils';
import { ManagerSelector } from '@/components/ManagerSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AgentAvatar } from '@/components/agent/AgentAvatar';
import { SendOnboardingLinkDialog } from '@/components/agent/dialogs/SendOnboardingLinkDialog';
import { OnboardingInvitesDialog } from '@/components/agent/dialogs/OnboardingInvitesDialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { flashAutofilled } from '@/lib/flashAutofilled';
import { toast } from 'sonner';
import {
  Users, Plus, Search, AlertCircle, Loader2, Ticket, Edit, Trash2,
  CheckCircle, XCircle, ArrowUpDown, ArrowUp, ArrowDown,
  ChevronLeft, ChevronRight, Filter, X, SlidersHorizontal, PhoneCall, Mail, Inbox,
} from 'lucide-react';

export default function AgentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isKR = isKRRole(user);
  const isPrincipal = isAgencyPrincipal(user);
  const canManageFullSSN = isKR || isPrincipal;
  // Phase 20.8.5 — show Communication column only to actors with governance
  // visibility. Agency Users and other roles never see this column.
  const showCommunicationColumn = isKR || isPrincipal;

  // Server-side state
  const [agents, setAgents] = useState([]);
  const [softphoneNpns, setSoftphoneNpns] = useState(new Set());
  const [total, setTotal] = useState(0);  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('first_name');
  const [sortDir, setSortDir] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    agency_id: 'all', agent_status: 'all',
    carrier_id: 'all', state: 'all',
  });
  const [filterOptions, setFilterOptions] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Dialog state
  const [agencies, setAgencies] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showSendLinkDialog, setShowSendLinkDialog] = useState(false);
  const [showInvitesDialog, setShowInvitesDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [npnCheckResult, setNpnCheckResult] = useState(null);
  const [formData, setFormData] = useState({
    npn: '', agency_id: '', first_name: '', last_name: '', email: '',
    phone: '', ssn_last4: '', ssn_full: '', date_of_birth: '',
    address_line1: '', address_line2: '', city: '', state: '', zip: '',
    agent_status: 'onboarding', manager_id: '', manager_name: '',
    preferred_language: 'en',
  });

  // Bulk selection
  const [selectedNpns, setSelectedNpns] = useState(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Debounce search
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Load filter options once
  useEffect(() => {
    agentAPI.getFilters().then(res => setFilterOptions(res.data)).catch(() => {});
    agencyAPI.list().then(res => setAgencies(res.data)).catch(() => {});
    if (searchParams.get('action') === 'add') setShowDialog(true);
  }, []);

  // Softphone badge — which agents currently have calling access (KR only).
  useEffect(() => {
    if (!isKR) return;
    voiceAPI.softphoneAgents()
      .then(res => setSoftphoneNpns(new Set(res.data?.npns || [])))
      .catch(() => {});
  }, [isKR]);

  // Load data whenever pagination/sort/filter/search changes
  useEffect(() => {
    loadData();
  }, [page, pageSize, sortBy, sortDir, debouncedSearch, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize, sort_by: sortBy, sort_dir: sortDir,
        search: debouncedSearch || undefined,
        agency_id: filters.agency_id !== 'all' ? filters.agency_id : undefined,
        agent_status: filters.agent_status !== 'all' ? filters.agent_status : undefined,
        carrier_id: filters.carrier_id !== 'all' ? filters.carrier_id : undefined,
        state: filters.state !== 'all' ? filters.state : undefined,
      };
      const res = await agentAPI.listPaginated(params);
      setAgents(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ agency_id: 'all', agent_status: 'all', carrier_id: 'all', state: 'all' });
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // CRUD handlers
  const checkNpn = async (npn) => {
    if (!npn || npn.length < 3) { setNpnCheckResult(null); return; }
    try { const r = await agentAPI.checkNpn(npn); setNpnCheckResult(r.data); } catch { setNpnCheckResult(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editingAgent && npnCheckResult?.exists && !npnCheckResult?.accessible) {
      toast.error('NPN exists outside your agency tree. Please create a ticket.'); return;
    }
    try {
      if (editingAgent) {
        const { npn, agency_id, ...updateData } = formData;
        await agentAPI.update(editingAgent.npn, updateData);
        toast.success('Agent updated');
      } else {
        await agentAPI.create(formData);
        toast.success('Agent created');
      }
      setShowDialog(false); resetForm(); loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Operation failed'); }
  };

  const resetForm = () => {
    setFormData({ npn: '', agency_id: getUserAgencyId(user) || '', first_name: '', last_name: '', email: '', phone: '', ssn_last4: '', ssn_full: '', date_of_birth: '', address_line1: '', address_line2: '', city: '', state: '', zip: '', agent_status: 'onboarding', manager_id: '', manager_name: '', preferred_language: 'en' });
    setEditingAgent(null); setNpnCheckResult(null);
  };

  const openEditDialog = (agent) => {
    setEditingAgent(agent);
    setFormData({ npn: agent.npn, agency_id: agent.agency_id, first_name: agent.first_name, last_name: agent.last_name, email: agent.email || '', phone: agent.phone || '', ssn_last4: '', ssn_full: '', date_of_birth: agent.date_of_birth || '', address_line1: agent.address_line1 || '', address_line2: agent.address_line2 || '', city: agent.city || '', state: agent.state || '', zip: agent.zip || '', agent_status: agent.agent_status, manager_id: agent.manager_id || '', manager_name: agent.manager_name || '', preferred_language: (agent.preferred_language || 'en').toLowerCase().slice(0, 2) });
    setShowDialog(true);
  };

  const handleDelete = async () => {
    if (!agentToDelete) return;
    try { await agentAPI.delete(agentToDelete.npn); toast.success('Agent deleted'); setShowDeleteDialog(false); setAgentToDelete(null); loadData(); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  // Bulk
  const toggleSelect = (npn) => setSelectedNpns(prev => { const n = new Set(prev); n.has(npn) ? n.delete(npn) : n.add(npn); return n; });
  const toggleSelectAll = () => selectedNpns.size === agents.length ? setSelectedNpns(new Set()) : setSelectedNpns(new Set(agents.map(a => a.npn)));

  const handleBulkAction = async () => {
    if (!bulkAction || selectedNpns.size === 0) return;
    setBulkLoading(true);
    try {
      const res = await agentAPI.bulkAction([...selectedNpns], bulkAction);
      if (res.data.success > 0) toast.success(`${res.data.success} agent(s) processed`);
      if (res.data.failed > 0) toast.error(`${res.data.failed} failed`);
      setSelectedNpns(new Set()); setShowBulkConfirm(false); setBulkAction(null); loadData();
    } catch (error) { toast.error('Bulk action failed'); }
    finally { setBulkLoading(false); }
  };

  const SortHeader = ({ field, label }) => {
    const active = sortBy === field;
    return (
      <button className="flex items-center gap-1 hover:text-slate-800 transition-colors group whitespace-nowrap"
        onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
        <span>{label}</span>
        {active ? (sortDir === 'asc' ? <ArrowUp size={13} className="text-blue-600" /> : <ArrowDown size={13} className="text-blue-600" />) :
          <ArrowUpDown size={13} className="text-slate-300 group-hover:text-slate-400" />}
      </button>
    );
  };

  return (
    <PageShell
      title="Agents"
      description="Manage agents, contracts, and carrier relationships across KR Enterprise Group."
      breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Business' }, { label: 'Agents' }]}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="add-agent-btn"><Plus size={16} className="mr-2" />Add Agent</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem
              onClick={() => { resetForm(); setShowDialog(true); }}
              data-testid="add-agent-manual-btn"
            >
              <Edit size={14} className="mr-2" /> Manual entry
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setShowSendLinkDialog(true)}
              data-testid="add-agent-send-link-btn"
            >
              <Mail size={14} className="mr-2" /> Send link to agent
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setShowInvitesDialog(true)}
              data-testid="view-invites-btn"
            >
              <Inbox size={14} className="mr-2" /> View onboarding invites
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
      testId="agents-page"
    >
      <div className="page-content">
        <Card>
          <CardContent className="pt-5">
            {/* Search + Filter Bar */}
            <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="agents-toolbar">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, NPN, agency..."
                  className="pl-9 h-9 text-sm" data-testid="agents-search-input" />
                {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-9 gap-1.5 shrink-0"
                  onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
                  <SlidersHorizontal size={14} />
                  <span className="hidden sm:inline">Filters</span>
                  {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center">{activeFilterCount}</span>}
                </Button>
                <div className="text-xs text-slate-500 px-2 whitespace-nowrap">{total} agent{total !== 1 ? 's' : ''}</div>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && filterOptions && (
              <div className="flex flex-wrap gap-2 p-3 mb-4 bg-slate-50 rounded-lg border border-slate-200" data-testid="filter-panel">
                <FilterSelect label="Agency" value={filters.agency_id} options={filterOptions.agencies} onChange={v => handleFilterChange('agency_id', v)} testId="filter-agency" />
                <FilterSelect label="Status" value={filters.agent_status} options={filterOptions.statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))} onChange={v => handleFilterChange('agent_status', v)} testId="filter-status" />
                <FilterSelect label="Carrier" value={filters.carrier_id} options={filterOptions.carriers} onChange={v => handleFilterChange('carrier_id', v)} testId="filter-carrier" />
                {filterOptions.states.length > 0 && (
                  <FilterSelect label="State" value={filters.state} options={filterOptions.states.map(s => ({ value: s, label: s }))} onChange={v => handleFilterChange('state', v)} testId="filter-state" />
                )}
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid="clear-filters-btn">Clear all</Button>
                )}
              </div>
            )}

            {/* Bulk Actions */}
            {isKR && selectedNpns.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 mb-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="bulk-actions-toolbar">
                <Badge variant="secondary" className="text-xs font-mono">{selectedNpns.size} selected</Badge>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  onClick={() => { setBulkAction('activate'); setShowBulkConfirm(true); }} data-testid="bulk-activate-btn"><CheckCircle size={13} /> Activate</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-amber-700 border-amber-300 hover:bg-amber-50"
                  onClick={() => { setBulkAction('deactivate'); setShowBulkConfirm(true); }} data-testid="bulk-deactivate-btn"><XCircle size={13} /> Deactivate</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-700 border-red-300 hover:bg-red-50"
                  onClick={() => { setBulkAction('delete'); setShowBulkConfirm(true); }} data-testid="bulk-delete-btn"><Trash2 size={13} /> Delete</Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => setSelectedNpns(new Set())} data-testid="clear-selection-btn">Clear</Button>
              </div>
            )}

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : agents.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Users size={40} className="mx-auto mb-3 text-slate-300" />
                <p className="font-medium text-slate-700 mb-1">{debouncedSearch || activeFilterCount > 0 ? 'No results found' : 'No agents found'}</p>
                <p className="text-sm">{debouncedSearch || activeFilterCount > 0 ? 'Try adjusting your search or filters.' : 'Add your first agent to get started.'}</p>
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 overflow-x-auto bg-white">
                <table className="w-full text-sm" data-testid="agents-table">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {isKR && (
                        <th className="px-3 py-3 text-left w-10">
                          <Checkbox checked={agents.length > 0 && selectedNpns.size === agents.length} onCheckedChange={toggleSelectAll} data-testid="select-all-agents" />
                        </th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><SortHeader field="first_name" label="Agent Name" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Agency</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden md:table-cell"><SortHeader field="npn" label="NPN" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xl:table-cell"><SortHeader field="email" label="Email" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Contracts</th>
                      {showCommunicationColumn && (
                        <th
                          className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xl:table-cell"
                          data-testid="agents-comm-header"
                        >
                          Communication
                        </th>
                      )}
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><SortHeader field="agent_status" label="Status" /></th>
                      <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 hidden xl:table-cell"><SortHeader field="updated_at" label="Last Updated" /></th>
                      <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 w-20">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {agents.map((agent) => (
                      <tr key={agent.npn} className="hover:bg-slate-50/80 transition-colors cursor-pointer group" data-testid={`agent-row-${agent.npn}`}
                        onClick={() => navigate(`/agents/${agent.npn}`)}>
                        {isKR && (
                          <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                            <Checkbox checked={selectedNpns.has(agent.npn)} onCheckedChange={() => toggleSelect(agent.npn)} data-testid={`select-agent-${agent.npn}`} />
                          </td>
                        )}
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <AgentAvatar
                              agent={agent}
                              size="w-9 h-9"
                              rounded="full"
                              textSize="text-xs"
                              testId={`agent-avatar-${agent.npn}`}
                            />
                            <div className="min-w-0">
                              <span className="font-medium text-slate-900 group-hover:text-blue-700 transition-colors truncate block" data-testid={`agent-name-${agent.npn}`}>
                                {agent.first_name} {agent.last_name}
                              </span>
                              <span className="block text-xs text-slate-400 lg:hidden truncate">{agent.agency_name || '-'}</span>
                              {softphoneNpns.has(agent.npn) && (
                                <Badge
                                  variant="outline"
                                  className="mt-1 inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium px-1.5 py-0"
                                  data-testid={`softphone-badge-${agent.npn}`}
                                  title="This agent can make and receive calls with the browser softphone"
                                >
                                  <PhoneCall size={10} /> Softphone
                                </Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-slate-600 hidden lg:table-cell">{agent.agency_name || '-'}</td>
                        <td className="px-3 py-3 hidden md:table-cell"><span className="font-mono text-xs text-slate-500">{agent.npn}</span></td>
                        <td className="px-3 py-3 text-slate-600 hidden xl:table-cell truncate max-w-[200px]">{agent.email || '-'}</td>
                        <td className="px-3 py-3 hidden lg:table-cell">
                          <span className="text-xs font-medium text-slate-600">{agent.contract_count || 0}</span>
                        </td>
                        {showCommunicationColumn && (
                          <td
                            className="px-3 py-3 hidden xl:table-cell"
                            data-testid={`agent-comm-cell-${agent.npn}`}
                          >
                            <div className="flex flex-col gap-0.5">
                              <CommunicationPolicyBadge
                                value={agent.effective_communication_policy}
                                short
                              />
                              {agent.has_agent_exception && (
                                <span
                                  className="text-[10px] uppercase tracking-wider text-indigo-600"
                                  data-testid={`agent-comm-exception-flag-${agent.npn}`}
                                >
                                  Exception
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-3"><StatusBadge status={agent.agent_status} /></td>
                        <td className="px-3 py-3 text-xs text-slate-400 hidden xl:table-cell">{formatDate(agent.updated_at || agent.created_at)}</td>
                        <td className="px-3 py-3 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/agents/${agent.npn}`)} data-testid={`view-agent-${agent.npn}`}><Edit size={14} /></Button>
                            {isKR && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                onClick={() => { setAgentToDelete(agent); setShowDeleteDialog(true); }} data-testid={`delete-agent-${agent.npn}`}><Trash2 size={14} /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-4 border-t border-slate-100" data-testid="agents-pagination">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>Rows per page</span>
                  <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setPage(1); }}>
                    <SelectTrigger className="h-7 w-[65px] text-xs" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[25, 50, 100].map(s => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">
                    {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page"><ChevronLeft size={14} /></Button>
                    {totalPages <= 5 ? (
                      Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(p)} data-testid={`page-${p}`}>{p}</Button>
                      ))
                    ) : (
                      <>
                        <Button variant={page === 1 ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(1)}>1</Button>
                        {page > 3 && <span className="text-xs text-slate-400 px-1">...</span>}
                        {Array.from({ length: 3 }, (_, i) => Math.min(Math.max(page - 1 + i, 2), totalPages - 1))
                          .filter((p, i, a) => a.indexOf(p) === i && p > 1 && p < totalPages)
                          .map(p => <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(p)}>{p}</Button>)}
                        {page < totalPages - 2 && <span className="text-xs text-slate-400 px-1">...</span>}
                        <Button variant={page === totalPages ? 'default' : 'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={() => setPage(totalPages)}>{totalPages}</Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page"><ChevronRight size={14} /></Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editingAgent ? 'Edit Agent' : 'Add New Agent'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {npnCheckResult?.exists && !npnCheckResult?.accessible && (
                <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription className="flex items-center justify-between"><span>This NPN exists outside your agency tree.</span><Button type="button" variant="outline" size="sm" className="ml-4" onClick={() => window.location.href = `/tickets?action=create&npn=${formData.npn}`}><Ticket size={14} className="mr-1" />Create Ticket</Button></AlertDescription></Alert>
              )}
              {npnCheckResult?.exists && npnCheckResult?.accessible && (
                <Alert><AlertCircle className="h-4 w-4" /><AlertDescription>Agent found in your downline: {npnCheckResult.agent?.first_name} {npnCheckResult.agent?.last_name}</AlertDescription></Alert>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="npn">NPN *</Label><Input id="npn" value={formData.npn} onChange={e => { setFormData(p => ({ ...p, npn: e.target.value })); checkNpn(e.target.value); }} placeholder="National Producer Number" disabled={!!editingAgent} required data-testid="npn-input" /></div>
                <div className="space-y-2"><Label htmlFor="agency_id">Agency *</Label>
                  <Select value={formData.agency_id} onValueChange={v => setFormData(p => ({ ...p, agency_id: v }))} disabled={!!editingAgent}>
                    <SelectTrigger data-testid="agency-select"><SelectValue placeholder="Select agency" /></SelectTrigger>
                    <SelectContent>{agencies.map(a => <SelectItem key={a.agency_id} value={a.agency_id}>{'--'.repeat(a.depth)} {a.agency_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>First Name *</Label><Input value={formData.first_name} onChange={e => setFormData(p => ({ ...p, first_name: e.target.value }))} required data-testid="first-name-input" /></div>
                <div className="space-y-2"><Label>Last Name *</Label><Input value={formData.last_name} onChange={e => setFormData(p => ({ ...p, last_name: e.target.value }))} required data-testid="last-name-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} data-testid="email-input" /></div>
                <div className="space-y-2"><Label>Phone</Label><Input value={formatPhoneInput(formData.phone)} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} data-testid="phone-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>SSN Last 4{editingAgent?.ssn_last4 && <span className="ml-2 text-xs text-slate-500">(Current: {editingAgent.ssn_last4})</span>}</Label>
                  <Input value={formData.ssn_last4} onChange={e => { let v = e.target.value.replace(/\D/g, '').slice(0, 4); setFormData(p => ({ ...p, ssn_last4: v })); }} maxLength={4} placeholder="1234" data-testid="ssn-last4-input" /></div>
                {canManageFullSSN && (
                  <div className="space-y-2"><Label>Full SSN{editingAgent?.has_full_ssn && <span className="ml-2 text-xs text-emerald-600">On file</span>}</Label>
                    <Input value={formData.ssn_full} onChange={e => { let v = e.target.value.replace(/\D/g, '').slice(0, 9); if (v.length > 5) v = `${v.slice(0,3)}-${v.slice(3,5)}-${v.slice(5)}`; else if (v.length > 3) v = `${v.slice(0,3)}-${v.slice(3)}`; setFormData(p => ({ ...p, ssn_full: v })); }} maxLength={11} placeholder="XXX-XX-XXXX" data-testid="ssn-full-input" /></div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth || ''}
                    onChange={e => setFormData(p => ({ ...p, date_of_birth: e.target.value }))}
                    data-testid="dob-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Preferred Language</Label>
                  <Select value={formData.preferred_language || 'en'} onValueChange={v => setFormData(p => ({ ...p, preferred_language: v }))}>
                    <SelectTrigger data-testid="preferred-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="es">Español</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500">Texts &amp; emails to this agent use this language.</p>
                </div>
              </div>
              {editingAgent && (
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={formData.agent_status} onValueChange={v => setFormData(p => ({ ...p, agent_status: v }))}>
                    <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="onboarding">Onboarding</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
              {(isKRRole(user) || isAccountingRole(user)) && (
                <div className="space-y-2">
                  <Label>Manager / Development Leader <span className="text-xs text-slate-500 ml-1">(optional)</span></Label>
                  <ManagerSelector
                    value={formData.manager_id}
                    valueName={formData.manager_name}
                    onChange={(id, name) => setFormData(p => ({ ...p, manager_id: id || '', manager_name: name || '' }))}
                    testId="agent-form-manager"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Address Line 1</Label>
                <AddressAutocomplete
                  value={formData.address_line1}
                  onChange={(v) => setFormData(p => ({ ...p, address_line1: v }))}
                  onPlaceSelected={(parsed) => {
                    setFormData(p => ({
                      ...p,
                      address_line1: parsed.street || parsed.formatted,
                      city: parsed.city || p.city,
                      state: parsed.state || p.state,
                      zip: parsed.zip || p.zip,
                    }));
                    flashAutofilled(['city-input', 'state-select', 'zip-input']);
                  }}
                  testId="address1-input"
                />
              </div>
              <div className="space-y-2"><Label>Address Line 2</Label><Input value={formData.address_line2} onChange={e => setFormData(p => ({ ...p, address_line2: e.target.value }))} data-testid="address2-input" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label>City</Label><Input value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} data-testid="city-input" /></div>
                <div className="space-y-2"><Label>State</Label>
                  <Select value={formData.state} onValueChange={v => setFormData(p => ({ ...p, state: v }))}>
                    <SelectTrigger data-testid="state-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{US_STATES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>ZIP</Label><Input value={formData.zip} onChange={e => setFormData(p => ({ ...p, zip: e.target.value }))} data-testid="zip-input" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={npnCheckResult?.exists && !npnCheckResult?.accessible} data-testid="submit-agent-btn">{editingAgent ? 'Save Changes' : 'Create Agent'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SendOnboardingLinkDialog
        open={showSendLinkDialog}
        onOpenChange={setShowSendLinkDialog}
        agencies={agencies}
        onSent={loadData}
      />

      <OnboardingInvitesDialog
        open={showInvitesDialog}
        onOpenChange={setShowInvitesDialog}
      />

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete Agent</AlertDialogTitle>
            <AlertDialogDescription>Are you sure you want to delete agent <strong>{agentToDelete?.first_name} {agentToDelete?.last_name}</strong> (NPN: {agentToDelete?.npn})?<span className="block mt-2 text-amber-600">Note: Agents with active carrier appointments cannot be deleted.</span></AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Dialog */}
      <AlertDialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkAction === 'delete' ? 'Delete' : bulkAction === 'activate' ? 'Activate' : 'Deactivate'} {selectedNpns.size} Agent(s)</AlertDialogTitle>
            <AlertDialogDescription>{bulkAction === 'delete' ? `Delete ${selectedNpns.size} agent(s)? Agents with active appointments will be skipped.` : `${bulkAction} ${selectedNpns.size} agent(s)?`}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel disabled={bulkLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkAction} disabled={bulkLoading}
              className={bulkAction === 'delete' ? 'bg-red-500 hover:bg-red-600' : bulkAction === 'activate' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'}
              data-testid="confirm-bulk-action-btn">{bulkLoading && <Loader2 size={14} className="animate-spin mr-1" />}Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

function FilterSelect({ label, value, options, onChange, testId }) {
  return (
    <div className="min-w-[140px]">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 text-xs bg-white" data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All {label}</SelectItem>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
